/**
 * parcoursProgressionGuard.js
 *
 * Garde-fous purs (sans Firebase ni localStorage) sur le chapitre courant du parcours.
 * Contexte : la sync garde toujours le chapitre le plus élevé entre localStorage et
 * Firestore (cliquet) — une valeur aberrante se propage donc pour toujours. Ces fonctions
 * détectent et corrigent un chapitre courant précédé d'une longue série de chapitres vides.
 */

// Nombre minimal de chapitres vides consécutifs pour parler de saut aberrant.
// Décision produit : un élève ne peut pas avoir un seul chapitre entièrement non joué
// (hors démarrage selon les années de pratique, géré par le chapitre de départ).
// Un chapitre dont les steps sont seulement tentés (attemptedSteps) compte comme actif.
export const MIN_EMPTY_RUN = 1;
// Départ inconnu (pas de drumYears) : le chapitre de départ est deviné à partir de l'activité,
// donc un démarrage tardif légitime (jusqu'à 7 chapitres sautés) ne doit pas être pris pour un saut.
export const MIN_EMPTY_RUN_UNKNOWN_START = 10;

function hasActivity(progression, chapitre) {
  const data = progression?.[`chapitre${chapitre}`];
  if (!data) return false;
  return Object.keys(data.completedSteps || {}).length > 0
    || Object.keys(data.attemptedSteps || {}).length > 0;
}

function activeChapitres(progression) {
  return Object.keys(progression || {})
    .map(k => /^chapitre(\d+)$/.exec(k))
    .filter(Boolean)
    .map(m => parseInt(m[1], 10))
    .filter(n => hasActivity(progression, n))
    .sort((a, b) => a - b);
}

/**
 * Chapitre de départ du parcours selon les années de batterie déclarées à l'inscription.
 * Doit rester identique à la table appliquée par initParcoursFromDrumYears.
 * @returns {number|null} null si l'info est absente
 */
export function startChapitreFromDrumYears(drumYears) {
  const years = parseInt(drumYears, 10);
  if (!years) return null;
  if (years >= 5) return 9;
  if (years >= 3) return 4;
  if (years >= 2) return 2;
  return 1;
}

/**
 * Chapitre courant précédé d'une longue série de chapitres vides ?
 * On parcourt, dans l'ordre, le chapitre de départ, les chapitres actifs puis le chapitre
 * courant ; la première série d'au moins MIN_EMPTY_RUN chapitres vides entre deux points
 * est un saut aberrant. L'activité située avant le chapitre de départ est ignorée
 * (ex. un élève de 5 ans qui a essayé le chapitre 1 avant de démarrer au 9).
 *
 * @param {Object} progression
 * @param {number|null} startChapitre - issu de startChapitreFromDrumYears ; si inconnu, on
 *   retombe sur le plus bas chapitre actif (la série avant lui est alors ignorée)
 * @param {number|null} minRunOverride - force le seuil (sert à la surveillance sans réparation)
 * @returns {number|null} chapitre vers lequel recaler (dernier chapitre actif avant la série,
 *                        jamais en deçà du chapitre de départ), ou null si la progression est saine
 */
export function findChapitreOvershoot(progression, startChapitre = null, minRunOverride = null) {
  const current = progression?.currentChapitre || 1;
  const allActive = activeChapitres(progression);
  const start = startChapitre ?? allActive[0];
  if (start === undefined) return null;

  const active = allActive.filter(n => n >= start);
  const points = new Set(active);
  if (current >= start) points.add(current);
  // Départ connu : le chapitre précédant le départ sert de borne basse virtuelle
  if (startChapitre !== null) points.add(start - 1);

  const minRun = minRunOverride ?? (startChapitre !== null ? MIN_EMPTY_RUN : MIN_EMPTY_RUN_UNKNOWN_START);
  const sorted = [...points].sort((a, b) => a - b);
  for (let i = 1; i < sorted.length; i++) {
    const emptyRun = sorted[i] - sorted[i - 1] - 1;
    if (emptyRun >= minRun) return Math.max(sorted[i - 1], start);
  }
  return null;
}

/**
 * Une avance de chapitre est valide uniquement vers le chapitre immédiatement suivant.
 */
export function isValidChapitreAdvance(currentChapitre, nextChapitre) {
  return nextChapitre === (currentChapitre || 1) + 1;
}

/**
 * Retire de la progression tous les chapitres strictement supérieurs à `target`
 * (mutation en place). Utilisé après un recalage pour que les chapitres "sautés" repartent vierges.
 * @returns {string[]} clés supprimées (ex. ['chapitre50', 'chapitre52'])
 */
export function dropChapitresAfter(progression, target) {
  const removed = [];
  for (const key of Object.keys(progression || {})) {
    const m = /^chapitre(\d+)$/.exec(key);
    if (m && parseInt(m[1], 10) > target) {
      delete progression[key];
      removed.push(key);
    }
  }
  return removed;
}
